<template>
  <div class="sponsorship-carousel">
    <!-- Loading State -->
    <div v-if="loading" class="overflow-auto w-full pb-12 -mb-12">
      <div class="flex gap-4 min-w-max">
        <div v-for="i in 4" :key="`shimmer-${i}`" class="w-[75vw] min-w-[75vw]">
          <div class="w-full relative aspect-[4/3]">
            <div class="rounded-3xl bg-slate-100 overflow-hidden w-full h-full">
              <div class="absolute w-full h-full shimmer-animation"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sponsorships Carousel -->
    <div v-else-if="sponsorships && sponsorships.length > 0" class="w-full">
      <SwipperView
        :slidesPerView="1.5"
        :nbTabs="sponsorships.length"
        :centeredSlides="sponsorships.length > 1"
        :autoPlay="sponsorships.length > 1 ? 6000 : 0"
        :loop="sponsorships.length > 1"
        :defaultTab="1"
      >
        <template #default="{ position }">
          <div class="w-full relative py-[28%]">
            <div class="absolute top-0 left-0 w-full h-full px-2 pb-2">
              <div
                class="relative w-full h-full rounded-3xl overflow-hidden shadow-lg cursor-pointer"
                @click="handleSponsorshipClick(currentSponsorship(position))"
              >
                <!-- Banner Image -->
                <ImgComponent
                  v-if="currentSponsorship(position)?.bannerImageUrl"
                  :src="currentSponsorship(position).bannerImageUrl"
                  class="w-full h-full object-cover"
                  alt="Sponsorship Banner"
                />
                <div
                  v-else
                  class="w-full h-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center"
                >
                  <div class="text-white text-center p-6">
                    <h3 class="font-bold text-xl mb-2">
                      {{ currentSponsorship(position)?.restaurant?.storeName || 'Sponsorship' }}
                    </h3>
                    <p class="text-sm opacity-90">
                      {{ getPromotionTitle(currentSponsorship(position)) }}
                    </p>
                  </div>
                </div>

                <!-- Overlay with restaurant info -->
                <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent p-4">
                  <div class="flex items-center gap-3">
                    <!-- Restaurant Logo -->
                    <div class="w-12 h-12 rounded-full bg-white overflow-hidden shadow-md">
                      <ImgComponent
                        v-if="currentSponsorship(position)?.restaurant?.logo"
                        :src="currentSponsorship(position).restaurant.logo"
                        class="w-full h-full object-cover"
                        alt=""
                      />
                      <div v-else class="w-full h-full bg-white flex items-center justify-center">
                        <span class="text-primary font-bold text-3xl font-ribeye uppercase">
                          {{ (currentSponsorship(position)?.restaurant?.storeName || 'R').charAt(0) }}
                        </span>
                      </div>
                    </div>

                    <!-- Restaurant Details -->
                    <div class="flex-1 min-w-0">
                      <h4 class="text-white font-bold truncate">
                        {{ currentSponsorship(position)?.restaurant?.storeName || 'Restaurant' }}
                      </h4>
                      <p class="text-white/90 text-sm truncate">
                        {{ getPromotionDescription(currentSponsorship(position)) }}
                      </p>
                    </div>

                    <!-- Arrow indicator -->
                    <div class="text-white">
                      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <!-- Promotion Badge -->
                <div
                  v-if="currentSponsorship(position)?.hasPromotion"
                  class="absolute top-3 right-3 bg-white px-3 py-1.5 rounded-full shadow-md"
                >
                  <span class="text-primary-600 font-bold text-sm">
                    {{ getPromotionBadge(currentSponsorship(position)) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </template>
      </SwipperView>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="px-4 py-8">
      <div class="bg-gradient-to-br from-red-50 to-red-100 rounded-3xl p-8 text-center border border-red-200">
        <!-- Icon -->
        <div class="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-200 to-red-300 flex items-center justify-center">
          <svg class="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <!-- Title -->
        <h3 class="text-lg font-bold text-red-800 mb-2">Oups !</h3>

        <!-- Description -->
        <p class="text-red-600 text-sm mb-6 max-w-xs mx-auto">
          Une erreur s'est produite lors du chargement des promotions.
        </p>

        <!-- Retry Button -->
        <button
          @click="retryLoad"
          class="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full transition-colors duration-200 shadow-md"
        >
          Réessayer
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <SectionEmptyState
      v-else
      class="px-4 py-8"
      title="Aucune promotion à la une"
      description="Les restaurants à la une apparaîtront ici lorsqu'ils seront disponibles."
      icon-type="recs"
      @refresh="retryLoad"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';

const homeStore = getStore('home');

const sponsorships = computed(() => homeStore.state.sponsorships.items);
const loading = computed(() => homeStore.state.sponsorships.loading);
const error = computed(() => homeStore.state.sponsorships.error);

function currentSponsorship(position) {
  if (sponsorships.value.length === 0) return null;
  return sponsorships.value[Math.abs(position) % sponsorships.value.length];
}

function getPromotionTitle(sponsorship) {
  if (!sponsorship?.promotionDetails) return 'Offre spéciale';
  if (typeof sponsorship.promotionDetails === 'string') return sponsorship.promotionDetails;
  return sponsorship.promotionDetails.title || 'Offre spéciale';
}

function getPromotionDescription(sponsorship) {
  if (!sponsorship?.promotionDetails) return 'Offre spéciale disponible';
  if (typeof sponsorship.promotionDetails === 'string') return sponsorship.promotionDetails;
  return sponsorship.promotionDetails.description || 'Offre spéciale disponible';
}

function getPromotionBadge(sponsorship) {
  if (!sponsorship?.hasPromotion) return 'Promo';
  if (typeof sponsorship.promotionDetails === 'string') return 'Promo';
  return sponsorship.promotionDetails.discount || 'Promo';
}

function handleSponsorshipClick(sponsorship) {
  if (!sponsorship?.restaurantId) return;

  bus.emit(constants.EVENTS.RESTAURANT.SHOW_RESTAURANT_DETAIL, {
    id: sponsorship.restaurantId
  });
}

function retryLoad() {
  bus.emit(constants.EVENTS.HOME.SPONSORSHIPS_LOAD);
}

onMounted(() => {
  bus.emit(constants.EVENTS.HOME.SPONSORSHIPS_LOAD);
});
</script>

<style scoped>
.sponsorship-carousel {
  position: relative;
}

.shimmer-animation {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
